/** Represents a value that can be either an error or a success. */
export class Result<A, E> {
    private constructor(private readonly _value: ['ok', A] | ['err', E]) {}

    /** Creates a result that represents a successful value. */
    static ok = <A, E = never>(value: A) => new Result<A, E>(['ok', value]);

    /** Creates a result that represents an error. */
    static error = <E, A = never>(value: E) => new Result<A, E>(['err', value]);

    /** Matches on whether a result is a success or error, passing the value to one of the provided functions. The return value of the function is passed back. */
    match<B, F>(ok: (value: A) => B, err: (value: E) => F): B | F {
        switch (this._value[0]) {
            case 'ok':
                return ok(this._value[1]);
            case 'err':
                return err(this._value[1]);
        }
    }

    /** True if the result is a success. */
    isOk(): this is Result<A, never> {
        return this._value[0] === 'ok';
    }

    /** True if the result is an error. */
    isError(): this is Result<never, E> {
        return this._value[0] === 'err';
    }

    get unsafeValue(): A {
        return this.match(
            (value) => value,
            (error) => {
                throw error;
            },
        );
    }

    /** Returns the value if the result is a success, otherwise throws an error. */
    get value(): A | E {
        return this._value[1];
    }

    /** Returns the value if the result is a success, otherwise returns the provided default value. */
    withDefault<B = A>(defaultValue: B): A | B {
        return this.match(
            (value) => value,
            () => defaultValue,
        );
    }

    /** Returns the value if the result is a success, otherwise returns `undefined`. */
    orUndefined(): A | undefined {
        return this.withDefault(undefined);
    }

    /** Returns the value if the result is a success, otherwise returns `null`. */
    orNull(): A | null {
        return this.withDefault(null);
    }

    /** Maps the success value of a result. */
    map<B>(f: (value: A) => B): Result<B, E> {
        return this.match(
            (value) => Result.ok(f(value)),
            (value) => Result.error(value),
        );
    }

    /** Maps the error value of a result. */
    mapError<F>(f: (value: E) => F): Result<A, F> {
        return this.match(
            (value) => Result.ok(value),
            (value) => Result.error(f(value)),
        );
    }

    /** Extends the current result, using the success value to create a new result in sequence. */
    flatMap<B>(f: (value: A) => Result<B, E>): Result<B, E> {
        return this.match(f, (value) => Result.error(value));
    }
}
